/**
 * Single source of truth for what a shopper SEES for a given product + pack
 * size, campaign-aware.
 *
 * Every surface (pack selector, headline price, affordability widget, prepaid
 * estimate, mobile sticky bar, quick-buy sheet) must resolve pricing through
 * here so they can never disagree. The amounts themselves come from
 * `checkout-pricing` (code defaults + admin-published overrides) and, when a
 * campaign is live, from `promo_campaigns` — the same rows the server re-reads
 * and enforces at payment-order creation.
 *
 * Nothing in this file invents a price.
 */
import {
  getPackPricingTable,
  PREPAID_DISCOUNT_RATE,
  type PackQty,
} from "@/lib/checkout-pricing";
import {
  packDisplayFromOverride,
  type ProductPageOverride,
} from "@/lib/productPageContent";
import { isCampaignLive, type PromoCampaign } from "./inaugural";

/** Presentation-only metadata (never prices). */
type Presentation = { unit: string; perBottle: number; dailyUse: string };

const PRESENTATION: Record<string, Presentation> = {
  "mag-absorb-pro": { unit: "tablets", perBottle: 60, dailyUse: "2 tablets/day" },
  "liver-biome-plus": { unit: "tablets", perBottle: 60, dailyUse: "2 tablets/day" },
  "ova-balance": { unit: "sachets", perBottle: 30, dailyUse: "1 sachet/day" },
};

const UNIT_SINGULAR: Record<string, string> = {
  tablets: "tablet",
  capsules: "capsule",
  sachets: "sachet",
};

export type PackOptionView = {
  qty: number;
  /** What the shopper pays for this pack right now. */
  price: number;
  /** Struck-through comparison amount (0 = none). */
  compareAt: number;
  /** What the comparison means, so copy can label it honestly. */
  compareBasis: "mrp" | "singles";
  save: number;
  badge: string;
  supply: string;
  unitsLabel: string;
  dailyUse: string;
  perUnitLabel: string;
  highlight: boolean;
  isCampaign: boolean;
  campaignName?: string;
  campaignId?: string;
  subLabel?: string;
};

/** Live campaign for this product, if any (already-filtered lists are fine). */
export function liveCampaignFor(
  campaign: PromoCampaign | null | undefined,
  handle: string,
  now: Date = new Date(),
): PromoCampaign | null {
  if (!campaign) return null;
  if (campaign.productHandle !== handle) return null;
  return isCampaignLive(campaign, now) ? campaign : null;
}

/**
 * The 1 / 2 / 3 pack choices to render. While a campaign is live, the pack
 * matching its qualifying quantity is REPLACED by the campaign option — there
 * is never a second selectable choice for the same number of bottles.
 */
export function getPackOptionViews(
  handle: string,
  override?: ProductPageOverride | null,
  campaign?: PromoCampaign | null,
  now: Date = new Date(),
): PackOptionView[] | null {
  const table = getPackPricingTable(handle);
  const pres = PRESENTATION[handle];
  if (!table || !pres) return null;

  const live = liveCampaignFor(campaign, handle, now);
  const ov = packDisplayFromOverride(override);
  const singlePrice = table[1].price;

  const qtys: PackQty[] = [1, 2, 3];
  return qtys.map((qty) => {
    const base = table[qty];
    const o = ov[qty];
    const units = qty * pres.perBottle;
    const unitsLabel = `${units} ${pres.unit}`;

    if (live && live.qualifyingQty === qty) {
      const price = live.offerPricePaise / 100;
      const compareAt = singlePrice * qty;
      return {
        qty,
        price,
        compareAt,
        compareBasis: "singles",
        save: Math.max(0, compareAt - price),
        badge: `${qty * 30}-DAY INAUGURAL OFFER`,
        supply: `${qty * 30}-day protocol`,
        unitsLabel,
        dailyUse: pres.dailyUse,
        perUnitLabel: perUnit(price, units, pres.unit),
        highlight: true,
        isCampaign: true,
        campaignName: live.name,
        campaignId: live.id,
        subLabel: `Get ${qty} bottles. Pay for ${qty - 1}.`,
      };
    }

    const price = o?.price && o.price > 0 ? o.price : base.price;
    const mrp = o?.mrp && o.mrp > 0 ? o.mrp : base.mrp;
    return {
      qty,
      price,
      compareAt: mrp > price ? mrp : 0,
      compareBasis: "mrp",
      save: Math.max(0, mrp - price),
      badge:
        o?.badge?.trim() ||
        (qty === 1 ? "STARTER" : qty === 2 ? "MOST POPULAR" : "BEST VALUE"),
      supply: o?.supply?.trim() || `${qty * 30}-day protocol`,
      unitsLabel,
      dailyUse: pres.dailyUse,
      perUnitLabel: o?.perUnitLabel?.trim() || perUnit(price, units, pres.unit),
      highlight: !live && qty === 2,
      isCampaign: false,
    };
  });
}

function perUnit(price: number, units: number, unit: string): string {
  const each = Math.round(price / Math.max(1, units));
  return `₹${each}/${UNIT_SINGULAR[unit] ?? "unit"}`;
}

/** Resolved pricing for one product at one quantity. Campaign-aware. */
export function getEffectivePack(
  handle: string,
  qty: number,
  campaign?: PromoCampaign | null,
  override?: ProductPageOverride | null,
  now: Date = new Date(),
): PackOptionView | null {
  const views = getPackOptionViews(handle, override, campaign, now);
  return views?.find((v) => v.qty === qty) ?? null;
}

/** Amount left after the standard gateway-applied prepaid benefit. */
export function prepaidEstimate(amount: number): number {
  return amount * (1 - PREPAID_DISCOUNT_RATE);
}

/** ₹2,582.10 — keeps paise only when they exist. */
export function formatInr(amount: number): string {
  const rounded = Math.round(amount * 100) / 100;
  return `₹${rounded.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(rounded) ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export { PREPAID_DISCOUNT_RATE };
